import React, { useState, useEffect } from "react";
import axiosClient, { linkImage } from "../../../axios-client";
import { useNavigate, useLocation } from "react-router-dom";
import { useStateContext } from "../../../context/ContextProvider";
import { useColorModeValue } from "@chakra-ui/react";

const CURRENCY_COLORS = {
  MAD: "#10b981", USD: "#3b82f6", EUR: "#8b5cf6", GBP: "#f59e0b",
  AED: "#06b6d4", SAR: "#ef4444", DZD: "#84cc16", TND: "#f97316", EGP: "#ec4899",
};

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StatCard = ({ label, value, icon, color, cardBg, textColor, borderColor, secondaryText }) => (
  <div style={{
    background: cardBg, borderRadius: 14, padding: "20px 24px",
    boxShadow: "0 2px 10px rgba(0,0,0,.07)", border: `1px solid ${borderColor}`,
    display: "flex", alignItems: "center", gap: 16,
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12,
      background: color + "1a", color, display: "flex",
      alignItems: "center", justifyContent: "center", fontSize: 22,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 26, fontWeight: 700, color: textColor }}>{value}</div>
      <div style={{ fontSize: 12, color: secondaryText, marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

const SectionTitle = ({ children, textColor }) => (
  <h2 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: textColor, display: "flex", alignItems: "center", gap: 8 }}>
    {children}
  </h2>
);

export default function BusinessDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useStateContext();
  const isAdminClient = user?.role === "AdminClient";
  const biz = location.state?.business;

  const [detail, setDetail] = useState(biz || null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [toast, setToast] = useState(null);

  // Dark mode colors
  const bg = useColorModeValue("#f8fafc", "#1a202c");
  const cardBg = useColorModeValue("#ffffff", "#2d3748");
  const textColor = useColorModeValue("#1e293b", "#f7fafc");
  const secondaryText = useColorModeValue("#64748b", "#94a3b8");
  const borderColor = useColorModeValue("#e2e8f0", "#4a5568");

  useEffect(() => {
    if (!biz) { navigate("/mes-businesses"); return; }

    // Refresh business detail
    axiosClient.get(`/businesses/${biz.id}/`).then((r) => setDetail(r.data)).catch(() => { });

    // Fetch recent orders for this business
    setLoadingOrders(true);
    axiosClient
      .get(`/commandes/?business=${biz.id}&page_size=5`)
      .then((r) => setOrders(r.data.results || []))
      .catch(() => { })
      .finally(() => setLoadingOrders(false));

    // Fetch recent products for this business
    setLoadingProducts(true);
    axiosClient
      .get(`/products/?business=${biz.id}&page_size=5`)
      .then((r) => setProducts(r.data.results || []))
      .catch(() => { })
      .finally(() => setLoadingProducts(false));
  }, [biz, navigate]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleToggle = () => {
    axiosClient.post(`/toggle-business-status/${detail.id}/`)
      .then((r) => {
        setDetail((prev) => ({ ...prev, is_active: r.data.is_active }));
        showToast(r.data.message);
      })
      .catch(() => showToast("Erreur lors du changement de statut.", "error"));
  };

  if (!biz || !detail) return null;

  const currencyColor = CURRENCY_COLORS[detail.currency] || "#6366f1";

  return (
    <div style={{ padding: "28px 32px", minHeight: "100vh", background: bg }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 24, zIndex: 9999,
          background: toast.type === "error" ? "#fef2f2" : "#f0fdf4",
          border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
          color: toast.type === "error" ? "#dc2626" : "#166534",
          borderRadius: 10, padding: "12px 20px", fontWeight: 500, fontSize: 14,
          boxShadow: "0 8px 30px rgba(0,0,0,.12)",
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button onClick={() => navigate("/mes-businesses")} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
            border: `1px solid ${borderColor}`, borderRadius: 9, background: cardBg,
            color: secondaryText, cursor: "pointer", fontWeight: 500, fontSize: 14,
          }}>
            <ArrowLeftIcon /> Retour
          </button>
          {detail.logo ? (
            <img src={`${linkImage}${detail.logo}`} alt={detail.name}
              style={{ width: 52, height: 52, borderRadius: 14, objectFit: "cover", border: `2px solid ${borderColor}` }} />
          ) : (
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, fontSize: 22,
            }}>
              {detail.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: textColor }}>{detail.name}</h1>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <span style={{
                padding: "2px 12px", borderRadius: 99, fontSize: 11, fontWeight: 700,
                background: currencyColor + "1a", color: currencyColor,
              }}>{detail.currency}</span>
              <span style={{
                padding: "2px 12px", borderRadius: 99, fontSize: 11, fontWeight: 700,
                background: detail.is_active ? "#dcfce7" : "#fef2f2",
                color: detail.is_active ? "#16a34a" : "#dc2626",
              }}>
                {detail.is_active ? "✓ Actif" : "✕ Inactif"}
              </span>
            </div>
          </div>
        </div>
        {/* Admin-only action buttons */}
        {isAdminClient && (
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={handleToggle}
              style={{
                padding: "9px 18px", border: `1px solid ${detail.is_active ? "#fca5a5" : "#86efac"}`,
                borderRadius: 9, background: detail.is_active ? "#fef2f2" : "#f0fdf4",
                color: detail.is_active ? "#dc2626" : "#16a34a",
                cursor: "pointer", fontWeight: 600, fontSize: 13,
              }}>
              {detail.is_active ? "Désactiver" : "Activer"}
            </button>
            <button
              onClick={() => navigate("/modifier-business", { state: { business: detail } })}
              style={{
                padding: "9px 18px", border: "none", borderRadius: 9,
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff",
                cursor: "pointer", fontWeight: 600, fontSize: 13,
              }}>
              ✏️ Modifier
            </button>
          </div>
        )}
      </div>

      {/* Description */}
      {detail.description && (
        <div style={{
          background: cardBg, borderRadius: 12, padding: "16px 20px", marginBottom: 24,
          border: `1px solid ${borderColor}`, color: secondaryText, fontSize: 14, lineHeight: 1.6,
        }}>
          {detail.description}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16, marginBottom: 32 }}>
        <StatCard label="Total Commandes" value={detail.orders_count ?? 0} icon="📦" color="#6366f1" cardBg={cardBg} textColor={textColor} borderColor={borderColor} secondaryText={secondaryText} />
        <StatCard label="Total Produits" value={detail.products_count ?? 0} icon="🛒" color="#10b981" cardBg={cardBg} textColor={textColor} borderColor={borderColor} secondaryText={secondaryText} />
        <StatCard label="Membres" value={detail.members_count ?? 0} icon="👥" color="#f59e0b" cardBg={cardBg} textColor={textColor} borderColor={borderColor} secondaryText={secondaryText} />
        <StatCard label="Devise" value={detail.currency} icon="💱" color={currencyColor} cardBg={cardBg} textColor={textColor} borderColor={borderColor} secondaryText={secondaryText} />
        <StatCard label="Statut" value={detail.is_active ? "Actif" : "Inactif"} icon={detail.is_active ? "✓" : "✕"} color={detail.is_active ? "#10b981" : "#ef4444"} cardBg={cardBg} textColor={textColor} borderColor={borderColor} secondaryText={secondaryText} />
      </div>


    </div>
  );
}

function StatusBadge({ value }) {
  const colors = {
    Confirmé: { bg: "#dcfce7", color: "#16a34a" },
    Annulé: { bg: "#fef2f2", color: "#dc2626" },
    Nouveau: { bg: "#eff6ff", color: "#2563eb" },
    default: { bg: "#f1f5f9", color: "#475569" },
  };
  const c = colors[value] || colors.default;
  return (
    <span style={{ padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>
      {value || "—"}
    </span>
  );
}

